import { EventPhase } from "./phase"

export type BlockId =
  | "checklist"
  | "views"
  | "quick"
  | "guests"
  | "attendance"
  | "emails"
  | "texts"
  | "seating"
  | "ticketing"
  | "questions"
  | "submissions"
  | "usage"

export type Layout = { order: BlockId[], hidden: BlockId[] }

// As stored; it may name cards the dashboard no longer has.
export type SavedLayout = { order: string[], hidden: string[] }

export const BLOCK_LABELS: Record<BlockId, string> = {
  checklist: "Setup Checklist",
  views: "Page Views",
  quick: "Quick Actions",
  guests: "Guests",
  attendance: "Attendance",
  emails: "Emails",
  texts: "Text Messages",
  seating: "Seating",
  ticketing: "Ticketing",
  questions: "Questions",
  submissions: "Recent Submissions",
  usage: "Event Usage",
}

const DEFAULT_LAYOUT: Record<EventPhase, Layout> = {
  "pre-event": {
    order: [
      "checklist", "views", "quick", "guests", "submissions", "usage",
      "emails", "texts", "questions", "ticketing", "seating", "attendance",
    ],
    hidden: [],
  },
  "day-of": {
    order: [
      "attendance", "quick", "checklist", "texts", "guests", "seating",
      "emails", "ticketing", "submissions", "questions", "views", "usage",
    ],
    hidden: [],
  },
  "post-event": {
    order: [
      "attendance", "emails", "guests", "ticketing", "questions", "submissions",
      "views", "texts", "seating", "quick", "usage", "checklist",
    ],
    hidden: ["checklist"],
  },
}

export const defaultLayout = (phase: EventPhase): Layout => ({
  order: [...DEFAULT_LAYOUT[phase].order],
  hidden: [...DEFAULT_LAYOUT[phase].hidden],
})

const isBlock = (id: string): id is BlockId => Object.hasOwn(BLOCK_LABELS, id)

// Drops blocks the dashboard no longer has and adds new ones after the block they
// follow by default, so a saved layout keeps working as cards come and go.
const isSavedLayout = (value: unknown): value is SavedLayout => (
  !!value
  && Array.isArray((value as SavedLayout).order)
  && Array.isArray((value as SavedLayout).hidden)
)

export const resolveLayout = (saved: unknown, phase: EventPhase): Layout => {
  if (!isSavedLayout(saved)) return defaultLayout(phase)

  const order = saved.order.filter(isBlock)
  const defaults = DEFAULT_LAYOUT[phase].order
  defaults.forEach((id, index) => {
    if (order.includes(id)) return

    const before = defaults.slice(0, index).reverse().find((other) => order.includes(other))
    order.splice(before ? order.indexOf(before) + 1 : 0, 0, id)
  })

  return { order, hidden: saved.hidden.filter(isBlock) }
}

// Dragged forward, a block lands after its target; dragged back, before it.
export const moveBlock = (order: BlockId[], dragged: BlockId, target: BlockId) => {
  if (dragged === target) return order

  const forward = order.indexOf(dragged) < order.indexOf(target)
  const rest = order.filter((id) => id !== dragged)
  const at = rest.indexOf(target) + (forward ? 1 : 0)

  return [...rest.slice(0, at), dragged, ...rest.slice(at)]
}

export const isDefaultLayout = (layout: Layout, phase: EventPhase) => (
  JSON.stringify(layout) === JSON.stringify(DEFAULT_LAYOUT[phase])
)
